/**
 * "View as <user>" (owner-only; docs/features/VIEW_AS.md). The target lives in sessionStorage,
 * so it is scoped to THIS TAB: a second tab stays the real person, and closing the tab ends it.
 *
 * The server does the substituting (api/auth.py reads `X-View-As` on google-user auth and
 * swaps the operator before every gate). This module only decides WHEN the header goes out:
 * googleApiAuth.ts attaches it to identity-origin requests, except the paths below, which
 * mirror the server's VIEW_AS_EXCLUDED_PREFIXES — per-user credentials (the browser
 * softphone, RingCentral OAuth, extension candidates, the roster self-row) are always the
 * real person's, or the admin's browser could register as the target's extension.
 */

const KEY = 'v4.viewas';
/** Set once a refusal has already caused a reload, so a second refusal cannot loop. */
const REFUSED_KEY = 'v4.viewas.refused';

export const VIEW_AS_HEADER = 'X-View-As';

export const VIEW_AS_EXCLUDED_PREFIXES = [
  '/api/dashboard/telephony/webphone',
  '/api/dashboard/telephony/oauth',
  '/api/dashboard/telephony/extensions/candidates',
  '/api/dashboard/team/me',
] as const;

export interface ViewAsTarget {
  email: string;
  name: string;
}

/** Page navigation, as an object so tests can spy on it (jsdom cannot navigate). */
export const viewAsNavigation = {
  reload: () => window.location.reload(),
  goHome: () => window.location.assign('/home'),
};

const storage = (): Storage | null => {
  try { return window.sessionStorage; } catch { return null; }
};

/** True for a path the header must never travel on (per-user credentials). */
export function isExcludedPath(path: string): boolean {
  return VIEW_AS_EXCLUDED_PREFIXES.some((p) => path === p || path.startsWith(p));
}

export function getViewAs(): ViewAsTarget | null {
  const raw = storage()?.getItem(KEY);
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Partial<ViewAsTarget>;
    if (!v || typeof v.email !== 'string' || !v.email.trim()) return null;
    return { email: v.email.trim().toLowerCase(), name: typeof v.name === 'string' ? v.name : '' };
  } catch {
    return null;
  }
}

export function setViewAs(target: ViewAsTarget): void {
  const s = storage();
  if (!s) return;
  s.setItem(KEY, JSON.stringify({ email: target.email.trim().toLowerCase(), name: target.name }));
  s.removeItem(REFUSED_KEY); // a fresh choice gets its own one reload on refusal
}

export function clearViewAs(): void {
  storage()?.removeItem(KEY);
}

/** Store the target and reload so every request, including the older Hub pages, goes out as them. */
export function enterViewAs(target: ViewAsTarget, reload: () => void = viewAsNavigation.reload): void {
  setViewAs(target);
  reload();
}

/** Drop the target and reload as the real person. */
export function exitViewAs(reload: () => void = viewAsNavigation.reload): void {
  clearViewAs();
  reload();
}

/** The two refusals api/auth.py sends (403): the grant is gone, or the target left the roster. */
export function isViewAsRefusal(detail: unknown): boolean {
  return typeof detail === 'string' && (detail.startsWith('View as') || detail.startsWith('That person'));
}

/**
 * A refusal arrived on a request that carried the header: clear the stale target and reload
 * ONCE so the tab comes back as the real person. Returns whether it reloaded. The second
 * refusal in a row (the flag is set) clears without reloading — a loop needs a reload each
 * turn, so denying the second one ends it.
 */
export function dropViewAsAfterRefusal(reload: () => void = viewAsNavigation.reload): boolean {
  clearViewAs();
  const s = storage();
  if (s?.getItem(REFUSED_KEY)) return false;
  s?.setItem(REFUSED_KEY, '1');
  reload();
  return true;
}
